import React, { useState, useRef, useEffect } from 'react';
import {
    FileText, Save, RotateCcw, CheckCircle, Loader2, Lock,
    ChevronLeft, ChevronRight, RefreshCw, Share2, Bookmark
} from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
    BLOG_TEMPLATES,
    BLOG_TONE_OPTIONS,
    BLOG_CTA_OPTIONS,
    BLOG_QUICK_ACTIONS
} from '../../../ai/prompts/contentPrompts';

const validateBlogPost = (content) => ({ isValid: true, errors: [], warnings: [] });

// Traffic Light Button Component
const TrafficLightButton = ({ color, hoverSymbol }) => {
    const [isHovered, setIsHovered] = useState(false);
    const colorClasses = {
        red: 'bg-red-500 hover:bg-red-600',
        yellow: 'bg-yellow-400 hover:bg-yellow-500',
        green: 'bg-green-500 hover:bg-green-600'
    };
    const symbolColor = { red: 'text-red-900', yellow: 'text-yellow-900', green: 'text-green-900' };

    return (
        <button
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            className={`w-4 h-4 rounded-full ${colorClasses[color]} transition-all duration-150 flex items-center justify-center shadow-sm hover:shadow-md`}
        >
            {isHovered && <span className={`text-[10px] font-bold ${symbolColor[color]}`}>{hoverSymbol}</span>}
        </button>
    );
};

// Blog Post Preview Component
const BlogPostPreview = ({
    content = null,
    title = null,
    isStreaming = false,
    isEditable = false,
    onContentChange = null,
    hasUnsavedChanges = false,
    onSave = null,
    onSaveToVault = null,
    onStartOver = null,
    isSavingToVault = false
}) => {
    const [isEditing, setIsEditing] = useState(false);
    const [saveSuccess, setSaveSuccess] = useState(false);
    const [isDraggingText, setIsDraggingText] = useState(false);
    const textareaRef = useRef(null);
    const textDragCounterRef = useRef(0);

    const defaultContent = `# The Future of AI in Podcasting

*How artificial intelligence is transforming the podcast industry*

---

## Introduction

The podcasting landscape is undergoing a **dramatic transformation**. With the integration of artificial intelligence, creators can now produce higher-quality content in a fraction of the time.

In this comprehensive guide, we'll explore:

- AI-powered editing tools
- Automated transcription services
- Voice cloning technology
- Content repurposing automation

## The Rise of AI Editing

Traditional podcast editing could take **10+ hours per episode**. Today's AI tools reduce that to minutes.

### Key Benefits

1. **Time Savings**: Cut editing time by 90%
2. **Consistency**: Uniform audio quality across episodes
3. **Cost Efficiency**: Reduce post-production costs

> "AI is not replacing podcasters-it's amplifying their creativity."
> 
> *- Industry Expert*

## Conclusion

The future of podcasting is here, and it's powered by AI.

---

*Published on February 18, 2024 · 8 min read*`;

    const displayContent = content || defaultContent;
    const displayTitle = title || 'Blog Post';

    // Custom markdown components for consistent styling
    const markdownComponents = {
        // Headings
        h1: ({ children }) => (
            <h1 className="text-3xl font-bold text-gray-900 mb-4 mt-8 first:mt-0 font-['Inter']">{children}</h1>
        ),
        h2: ({ children }) => (
            <h2 className="text-2xl font-bold text-gray-900 mb-3 mt-8 font-['Inter']">{children}</h2>
        ),
        h3: ({ children }) => (
            <h3 className="text-xl font-bold text-gray-900 mb-2 mt-6 font-['Inter']">{children}</h3>
        ),
        h4: ({ children }) => (
            <h4 className="text-lg font-bold text-gray-900 mb-2 mt-4 font-['Inter']">{children}</h4>
        ),
        // Paragraphs
        p: ({ children }) => (
            <p className="text-gray-700 leading-relaxed mb-4 font-['Inter']">{children}</p>
        ),
        // Bold text
        strong: ({ children }) => (
            <strong className="font-bold text-gray-900">{children}</strong>
        ),
        // Italic text
        em: ({ children }) => (
            <em className="italic text-gray-700">{children}</em>
        ),
        // Inline code
        code: ({ inline, className, children }) => {
            if (inline) {
                return (
                    <code className="px-1.5 py-0.5 bg-slate-800 text-emerald-400 rounded text-sm font-mono">
                        {children}
                    </code>
                );
            }
            return (
                <code className={className}>{children}</code>
            );
        },
        // Code blocks
        pre: ({ children }) => (
            <div className="my-6 rounded-xl overflow-hidden shadow-lg border border-slate-700">
                <pre className="bg-slate-900 p-4 overflow-x-auto">
                    {children}
                </pre>
            </div>
        ),
        // Bullet lists
        ul: ({ children }) => (
            <ul className="space-y-2 mb-6 ml-1">{children}</ul>
        ),
        // Numbered lists
        ol: ({ children }) => (
            <ol className="space-y-2 mb-6 ml-1 list-decimal list-inside">{children}</ol>
        ),
        // List items
        li: ({ children }) => (
            <li className="flex items-start gap-3 text-gray-700 leading-relaxed">
                <span className="w-1.5 h-1.5 rounded-full bg-indigo-500 mt-2.5 flex-shrink-0"></span>
                <span className="flex-1">{children}</span>
            </li>
        ),
        // Blockquotes
        blockquote: ({ children }) => (
            <blockquote className="my-6 pl-4 pr-4 py-4 border-l-4 border-indigo-500 bg-indigo-50/50 rounded-r-xl">
                {children}
            </blockquote>
        ),
        // Horizontal rules
        hr: () => (
            <hr className="my-8 border-gray-200" />
        ),
        // Links
        a: ({ href, children }) => (
            <a href={href} className="text-indigo-600 hover:underline font-medium">{children}</a>
        ),
    };

    const handleTextChange = (e) => {
        if (onContentChange) onContentChange(e.target.value);
    };

    const handleContentClick = () => {
        if (isEditable && !isStreaming) setIsEditing(true);
    };

    const handleBlur = () => setIsEditing(false);

    useEffect(() => {
        if (isEditing && textareaRef.current) textareaRef.current.focus();
    }, [isEditing]);

    const handleTextDragEnter = (e) => {
        e.preventDefault();
        e.stopPropagation();
        textDragCounterRef.current++;
        if (textDragCounterRef.current === 1) setIsDraggingText(true);
    };

    const handleTextDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        textDragCounterRef.current--;
        if (textDragCounterRef.current === 0) setIsDraggingText(false);
    };

    const handleTextDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleTextDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        textDragCounterRef.current = 0;
        setIsDraggingText(false);

        if (e.dataTransfer.types.includes('application/json')) {
            try {
                const jsonData = e.dataTransfer.getData('application/json');
                const artifact = JSON.parse(jsonData);
                if (artifact?.type === 'vault-asset' && artifact.assetType === 'text' && artifact.content) {
                    if (onContentChange) onContentChange(artifact.content);
                }
            } catch (err) { console.error('Failed to parse vault asset:', err); }
        }

        if (e.dataTransfer.types.includes('text/plain')) {
            const text = e.dataTransfer.getData('text/plain');
            if (text && onContentChange) onContentChange(text);
        }
    };

    return (
        <div className="flex flex-col h-full bg-slate-900 font-['Inter']">
            {/* Action Bar */}
            {isEditable && content && (
                <div className="w-full px-4 py-3 border-b border-slate-700/50 bg-slate-800/50">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className="flex items-center gap-1.5 text-xs text-slate-400">
                                <FileText size={14} />
                                <span>{displayContent.split(/\s+/).length.toLocaleString()} words</span>
                            </div>
                            <div className="w-px h-4 bg-slate-600" />
                            {saveSuccess ? (
                                <><CheckCircle size={14} className="text-green-500" /><span className="text-xs text-green-400 font-medium">Saved!</span></>
                            ) : hasUnsavedChanges ? (
                                <><div className="w-2 h-2 rounded-full bg-amber-500 animate-pulse" /><span className="text-xs text-amber-400 font-medium">Unsaved</span></>
                            ) : (
                                <><CheckCircle size={14} className="text-green-500" /><span className="text-xs text-green-400 font-medium">Ready</span></>
                            )}
                        </div>
                        <div className="flex items-center gap-2">
                            <button onClick={onStartOver} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-slate-400 hover:bg-slate-700 hover:text-slate-200 transition-all">
                                <RotateCcw size={14} />Start Over
                            </button>
                            <button onClick={async () => { if (onSaveToVault) { const success = await onSaveToVault(); if (success) { setSaveSuccess(true); setTimeout(() => setSaveSuccess(false), 2000); } } }} disabled={isSavingToVault || !content} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${isSavingToVault ? 'bg-slate-700 text-slate-400 cursor-not-allowed' : 'bg-indigo-600 text-white hover:bg-indigo-500 shadow-sm'}`}>
                                {isSavingToVault ? <><Loader2 size={14} className="animate-spin" />Saving...</> : <><Save size={14} />Save</>}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Safari Browser Frame */}
            <div className="flex-1 flex flex-col min-h-0">
                {/* macOS Window Chrome */}
                <div className="flex-shrink-0 bg-gradient-to-b from-slate-700 to-slate-800 rounded-t-xl px-4 py-3">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2.5">
                            <TrafficLightButton color="red" hoverSymbol="×" />
                            <TrafficLightButton color="yellow" hoverSymbol="-" />
                            <TrafficLightButton color="green" hoverSymbol="+" />
                        </div>

                        {/* Safari Address Bar */}
                        <div className="flex-1 mx-6 max-w-3xl">
                            <div className="flex items-center gap-3 bg-slate-900/60 backdrop-blur-xl rounded-lg px-4 py-2 border border-slate-600/50">
                                <button className="p-1.5 text-slate-400 hover:text-slate-200 transition-colors"><ChevronLeft size={16} /></button>
                                <button className="p-1.5 text-slate-400 hover:text-slate-200 transition-colors"><ChevronRight size={16} /></button>
                                <div className="flex-1 flex items-center gap-2 px-2">
                                    <Lock size={14} className="text-green-500 flex-shrink-0" />
                                    <span className="text-sm text-slate-300 font-mono truncate">podguestly.com/blog/{displayTitle.toLowerCase().replace(/\s+/g, '-')}</span>
                                </div>
                                <button className="p-1.5 text-slate-400 hover:text-slate-200 transition-colors"><RefreshCw size={16} /></button>
                                <button className="p-1.5 text-slate-400 hover:text-slate-200 transition-colors"><Share2 size={16} /></button>
                                <button className="p-1.5 text-slate-400 hover:text-slate-200 transition-colors"><Bookmark size={16} /></button>
                            </div>
                        </div>
                        <div className="w-20"></div>
                    </div>
                </div>

                {/* Content Area */}
                <div className="flex-1 overflow-y-auto bg-white rounded-b-xl" onDragEnter={handleTextDragEnter} onDragLeave={handleTextDragLeave} onDragOver={handleTextDragOver} onDrop={handleTextDrop}>
                    {isEditing && isEditable && !isStreaming ? (
                        <textarea ref={textareaRef} value={displayContent} onChange={handleTextChange} onBlur={handleBlur} className={`w-full h-full min-h-[500px] p-8 text-sm text-gray-900 leading-relaxed whitespace-pre-wrap resize-none border-0 outline-none font-mono ${isDraggingText ? 'bg-indigo-50 ring-2 ring-indigo-300' : ''}`} placeholder="Write your blog post here..." />
                    ) : (
                        <div onClick={handleContentClick} className={`p-8 max-w-3xl mx-auto transition-all ${isDraggingText ? 'bg-indigo-50 min-h-[300px] flex items-center justify-center' : ''} ${isEditable && !isStreaming ? 'cursor-text' : ''}`}>
                            {isDraggingText ? (
                                <div className="text-center">
                                    <div className="w-12 h-12 mx-auto mb-3 rounded-xl bg-indigo-100 flex items-center justify-center"><FileText size={24} className="text-indigo-600" /></div>
                                    <span className="text-indigo-600 font-semibold text-base">Drop text content here</span>
                                    <p className="text-indigo-400 text-xs mt-1">Release to insert text</p>
                                </div>
                            ) : (
                                <article className="prose prose-indigo max-w-none">
                                    <ReactMarkdown
                                        remarkPlugins={[remarkGfm]}
                                        components={markdownComponents}
                                    >
                                        {displayContent}
                                    </ReactMarkdown>
                                    {isStreaming && <span className="inline-block w-1.5 h-5 bg-indigo-500 animate-pulse ml-0.5" />}
                                </article>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export { BlogPostPreview };
export default BlogPostPreview;